/**
 * Markets: which address serves which language and default currency (backend:
 * shop/logic/markets.py). The URL decides the language — a market's own address always renders in
 * its language, whatever the stag_locale cookie says.
 *
 * Pure functions only (no Next/React imports): proxy.ts, server components and client components
 * all share them, and `npm test` runs them directly under node.
 */

export type MarketUrlType = 'default' | 'prefix' | 'domain';

export interface Market {
  id: string;
  name: string;
  locale: string;
  url_type: MarketUrlType;
  domain: string | null;
  prefix: string | null;
  currency: string;
  currencies: string[];
  active: boolean;
}

export interface MarketMatch {
  market: Market;
  /** The path the app's own routes understand — the market prefix removed. */
  internalPath: string;
  /** True when the request path carried the market's /xx/ prefix. */
  prefixed: boolean;
}

export function normalizeHost(host: string | null | undefined): string {
  return (host || '').split(':')[0].trim().toLowerCase().replace(/^www\./, '');
}

/**
 * The shop's markets from /site-config. The backend always sends them (derived from the older
 * default_locale / currency / domain_locale_map fields until an admin saves markets); the same
 * derivation runs here for a backend that predates markets.
 */
export function getMarkets(config: any): Market[] {
  const fromApi = Array.isArray(config?.markets) ? (config.markets as Market[]) : [];
  if (fromApi.some((market) => market?.url_type === 'default')) return fromApi;

  const locale = config?.default_locale || 'pl';
  const currency = config?.currency || 'PLN';
  const supported: string[] = Array.isArray(config?.supported_currencies) && config.supported_currencies.length
    ? config.supported_currencies
    : [currency];
  const currencies = supported.includes(currency) ? supported : [currency, ...supported];
  const markets: Market[] = [{
    id: 'default', name: 'Główny', locale, url_type: 'default', domain: null, prefix: null,
    currency, currencies, active: true,
  }];
  for (const [rawHost, entry] of Object.entries((config?.domain_locale_map || {}) as Record<string, any>)) {
    const domain = normalizeHost(rawHost);
    if (!domain || !entry || typeof entry !== 'object') continue;
    const domainCurrency = entry.currency || currency;
    markets.push({
      id: domain.replace(/[^a-z0-9-]/g, '-'), name: domain, locale: entry.locale || locale, url_type: 'domain',
      domain, prefix: null, currency: domainCurrency,
      currencies: currencies.includes(domainCurrency) ? currencies : [domainCurrency, ...currencies], active: true,
    });
  }
  return markets;
}

function defaultMarket(markets: Market[]): Market {
  return markets.find((market) => market.url_type === 'default') || markets[0];
}

/**
 * Which market a request belongs to. A domain market owns its whole host; on every other host the
 * first path segment may select a prefix market, otherwise it's the default market.
 */
export function resolveMarket(markets: Market[], host: string | null | undefined, pathname: string): MarketMatch {
  const active = markets.filter((market) => market.active !== false);
  const normalizedHost = normalizeHost(host);

  const domainMarket = active.find((market) => market.url_type === 'domain' && market.domain === normalizedHost);
  if (domainMarket) return { market: domainMarket, internalPath: pathname, prefixed: false };

  const [, firstSegment = '', ...rest] = pathname.split('/');
  const prefixMarket = active.find((market) => market.url_type === 'prefix' && market.prefix === firstSegment.toLowerCase());
  if (prefixMarket) {
    const remainder = rest.join('/');
    return { market: prefixMarket, internalPath: `/${remainder}`, prefixed: true };
  }

  return { market: defaultMarket(active.length ? active : markets), internalPath: pathname, prefixed: false };
}

/**
 * The language a request renders in. A market's own language wins — except on the default market,
 * where a visitor may still have picked (cookie) a supported language that has no address of its
 * own yet. That legacy behaviour isn't crawlable, but it keeps every shop that hasn't set up
 * markets working exactly as before.
 */
export function resolveLocale(match: MarketMatch, markets: Market[], cookieLocale: string | null | undefined, supportedLocales: string[]): string {
  const { market } = match;
  if (market.url_type !== 'default' || !cookieLocale || cookieLocale === market.locale) return market.locale;
  if (!supportedLocales.includes(cookieLocale)) return market.locale;
  const hasOwnAddress = markets.some((other) => other.active !== false && other.locale === cookieLocale);
  return hasOwnAddress ? market.locale : cookieLocale;
}

/** The visitor's chosen currency when this market sells in it, otherwise the market's default. */
export function resolveCurrency(market: Market, cookieCurrency: string | null | undefined): string {
  return cookieCurrency && market.currencies.includes(cookieCurrency) ? cookieCurrency : market.currency;
}

/**
 * A visitor whose chosen language (cookie) has its own prefix on this host, landing on an
 * unprefixed default-market URL — usually an internal link that doesn't carry the prefix. Returns
 * the market to redirect to. Crawlers send no cookie, so they always get the URL they asked for.
 */
export function stickyPrefixMarket(match: MarketMatch, markets: Market[], cookieLocale: string | null | undefined): Market | null {
  if (match.prefixed || match.market.url_type !== 'default' || !cookieLocale || cookieLocale === match.market.locale) return null;
  return markets.find((market) => market.active !== false && market.url_type === 'prefix' && market.locale === cookieLocale) || null;
}

/** `path` (an app-internal path like "/kontakt") as it's addressed in `market`. */
export function localizePath(market: Market, path: string): string {
  if (market.url_type !== 'prefix' || !market.prefix) return path;
  if (!path || path === '/') return `/${market.prefix}`;
  return `/${market.prefix}${path.startsWith('/') ? path : `/${path}`}`;
}

/** Origin a market's pages live on: its own domain, or the shop's main origin. */
export function marketOrigin(market: Market, primaryOrigin: string): string {
  if (market.url_type !== 'domain' || !market.domain) return primaryOrigin.replace(/\/$/, '');
  let protocol = 'https:';
  try { protocol = new URL(primaryOrigin).protocol; } catch { /* keep https */ }
  return `${protocol}//${market.domain}`;
}
